import { useState, useEffect, useRef } from "react";
import "./chartGuide.css";

function computeBelowPlacement({ rect, bubbleWidth, bubbleHeight, bottomNavbarHeight }) {
  const margin = 12;
  const gap = 24;
  const maxTop = window.innerHeight - bottomNavbarHeight - bubbleHeight - margin;
  const maxLeft = window.innerWidth - bubbleWidth - margin;

  const top = Math.min(Math.max(rect.bottom + gap, margin), Math.max(maxTop, margin));

  const centeredLeft = rect.left + rect.width / 2 - bubbleWidth / 2;
  const left = Math.min(Math.max(centeredLeft, margin), Math.max(maxLeft, margin));

  const ARROW_SIZE = 20;
  const targetCenter = rect.left + rect.width / 2;
  const rawPercent = ((targetCenter - left) / bubbleWidth) * 100;
  const limit = (ARROW_SIZE / bubbleWidth) * 100 + 4;
  const arrowOffset = `${Math.min(Math.max(rawPercent, limit), 100 - limit)}%`;

  return { top, left, arrow: "top", arrowOffset };
}

const ChartGuideSteps = ({ setShowGuide }) => {
  const steps = [
    {
      id: 1,
      target: "chart-guide-watchlist-selector",
      text: "Your active Watchlist appears here. Click the list name to switch between your personal and QUSHi Curated Lists.",
    },
    {
      id: 2,
      target: "chart-guide-timeframe-wheel",
      text: "Choose how much time each candle represents. Click the timeframe menu to select a different interval and change your view of the market.",
    },
    {
      id: 3,
      target: "chart-guide-drawing-tools",
      text: "Mark up your chart with trendlines and other drawing tools. Use them to map levels, structure, and ideas.",
    },
    {
      id: 6,
      target: "chart-guide-share",
      text: "Share what you see. Post your chart to the Feed with drawings, indicators, and a caption to show how you’re reading the market.",
    },
  ];

  const [stepIndex, setStepIndex] = useState(0);
  const [bubblePosition, setBubblePosition] = useState({ top: -1000, left: -1000 });
  const [arrowPosition, setArrowPosition] = useState("bottom");
  const [arrowOffset, setArrowOffset] = useState("50%");
  const [isPositioned, setIsPositioned] = useState(false);
  const bubbleRef = useRef(null);
  const current = steps[stepIndex];

  const skipToNextStepRef = useRef(null);
  skipToNextStepRef.current = () => {
    if (stepIndex < steps.length - 1) {
      setIsPositioned(false);
      setBubblePosition({ top: -1000, left: -1000 });
      setStepIndex(stepIndex + 1);
      return;
    }
    setShowGuide(false);
  };

  useEffect(() => {
    const prevHighlighted = document.querySelectorAll('.chart-guide-highlight');
    prevHighlighted.forEach(el => el.classList.remove('chart-guide-highlight'));

    const targetElement = document.getElementById(current.target);
    if (targetElement) {
      targetElement.classList.add('chart-guide-highlight');
    }

    return () => {
      const highlightedElement = document.getElementById(current.target);
      if (highlightedElement) {
        highlightedElement.classList.remove('chart-guide-highlight');
      }
    };
  }, [current.target]);

  // Calculate position relative to target element and auto-scroll
  useEffect(() => {
    const isMobile = window.innerWidth <= 768;
    const bottomNavbarHeight = isMobile ? 64 : 0;
    const compute = () => {
      const targetElement = document.getElementById(current.target);
      if (targetElement) {
        const rect = targetElement.getBoundingClientRect();
        const bubbleElement = bubbleRef.current;
        const bubbleWidth = bubbleElement?.offsetWidth || (isMobile ? 280 : 320);
        const bubbleHeight = bubbleElement?.offsetHeight || (isMobile ? 200 : 180);

        const { top, left, arrow, arrowOffset } = computeBelowPlacement({
          rect,
          bubbleWidth,
          bubbleHeight,
          bottomNavbarHeight,
        });

        setBubblePosition({ top, left });
        setArrowPosition(arrow);
        setArrowOffset(arrowOffset);
        setIsPositioned(true);
      }
    };

    let settleTimer;
    const scrollAndPosition = () => {
      const targetElement = document.getElementById(current.target);
      if (!targetElement) {
        if (process.env.NODE_ENV !== 'production') {
          console.warn(`[ChartGuideSteps] no element #${current.target}, skipping step`);
        }
        skipToNextStepRef.current();
        return;
      }

      const rect = targetElement.getBoundingClientRect();
      const comfortablyVisible =
        rect.top > 80 && rect.bottom < window.innerHeight - bottomNavbarHeight - 120;
      if (!comfortablyVisible) {
        targetElement.scrollIntoView({
          behavior: 'auto',
          block: 'nearest',
          inline: 'nearest'
        });
      }

      compute();
      settleTimer = setTimeout(compute, isMobile ? 150 : 220);
    };

    const timer = setTimeout(scrollAndPosition, 60);

    window.addEventListener('resize', compute, { passive: true });
    window.addEventListener('scroll', compute, { passive: true });
    return () => {
      clearTimeout(timer);
      clearTimeout(settleTimer);
      window.removeEventListener('resize', compute);
      window.removeEventListener('scroll', compute);
    };
  }, [current.target]);

  const handleNext = () => {
    if (stepIndex < steps.length - 1) {
      setIsPositioned(false);
      setBubblePosition({ top: -1000, left: -1000 });
      setStepIndex(stepIndex + 1);
      return;
    }

    setShowGuide(false);
  };

  const handleSkip = () => {
    const highlighted = document.querySelectorAll('.chart-guide-highlight');
    highlighted.forEach(el => el.classList.remove('chart-guide-highlight'));
    setShowGuide(false);
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      const highlighted = document.querySelectorAll('.chart-guide-highlight');
      highlighted.forEach(el => el.classList.remove('chart-guide-highlight'));
    };
  }, []);

  return (
    <>
      {/* Backdrop */}
      <div className="chart-guide-backdrop" onClick={handleSkip} />

      {/* Guide Bubble */}
      <div
        ref={bubbleRef}
        className={`chart-guide-bubble dynamic-position arrow-${arrowPosition} step-${stepIndex + 1}`}
        style={{
          top: bubblePosition.top,
          left: bubblePosition.left,
          opacity: isPositioned ? 1 : 0,
          '--arrow-offset': arrowOffset,
          transition: 'opacity 0.2s ease-in-out'
        }}
      >
        <div className="guide-step-indicator">
          Step {stepIndex + 1} of {steps.length}
        </div>
        <div className="guide-text">{current.text}</div>

        <div className="guide-buttons">
          <button className="next-btn" onClick={handleNext}>
            {stepIndex === steps.length - 1 ? 'Done' : 'Next'}
          </button>
          <button className="skip-btn" onClick={handleSkip}>
            Skip
          </button>
        </div>
      </div>
    </>
  );
};

export default ChartGuideSteps;